import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonContent,
  IonMenu,
  IonMenuToggle,
  useIonRouter,
} from '@ionic/react';
import { User, SlidersHorizontal, Users, ChefHat, Receipt, Share2, PiggyBank } from 'lucide-react';
import { logout } from '../../services/authService';
import { useProfileStore } from '../../store/useProfileStore';
import { useAuthStore } from '../../store/useAuthStore';
import { isChef } from '../../types/profile';
import { useMyListingsCounts } from '../../hooks/useListings';
import { useMyEarnings } from '../../hooks/useEarnings';
import { sumEarnedCents } from '../../utils/earnings';
import { formatPriceCents } from '../../utils/currency';
import { ChefApplicationModal } from '../onboarding/ChefApplicationModal';
import { LanguageSwitcher } from '../profile/LanguageSwitcher';
import { ShareQRModal } from './ShareQRModal';
import { Avatar } from './Avatar';

// Ionic menu id — TopBar's avatar button opens this via
// `menuController.open(PROFILE_MENU_ID)`. Kept here so the id has a single
// source of truth shared between the menu and its trigger.
export const PROFILE_MENU_ID = 'profile-menu';

interface NavItem {
  path: string;
  labelKey: string;
  Icon: React.ComponentType<{ className?: string }>;
}

// Nav rows. My Profile leads (the header above is display-only), followed by
// the broken-out section pages. Each routes to its own page.
const NAV_ITEMS: readonly NavItem[] = [
  { path: '/profile', labelKey: 'menu.profile', Icon: User },
  { path: '/settings', labelKey: 'menu.settings', Icon: SlidersHorizontal },
  { path: '/connections', labelKey: 'menu.connections', Icon: Users },
  { path: '/my-kitchen', labelKey: 'menu.kitchen', Icon: ChefHat },
  { path: '/my-earnings', labelKey: 'menu.earnings', Icon: PiggyBank },
  { path: '/my-orders', labelKey: 'menu.orders', Icon: Receipt },
] as const;

/**
 * Right-side navigation drawer opened from the TopBar avatar. Replaces the
 * old single "everything" ProfilePage with a hub: a profile header plus one
 * button per broken-out section page. Rendered once at the app root and
 * addressed by `PROFILE_MENU_ID`.
 */
export const ProfileMenu: React.FC = () => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const router = useIonRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const [showChefModal, setShowChefModal] = useState(false);
  // Share popup (auth-only, same reusable dialog as the home-feed share card).
  const [qrOpen, setQrOpen] = useState(false);

  // Publishing is open to everyone now (the chef gate sits at the end of the
  // publish flow), so a non-chef can own drafts and listings. Counts tell us
  // whether they have a kitchen worth linking to. Only fetched when the answer
  // could change anything: chefs always get the row, and the endpoint is
  // user-scoped so logged-out mounts must not call it.
  const { data: listingCounts } = useMyListingsCounts(
    isAuthenticated && !isChef(activeProfile),
  );

  // Backs the total-earned sub line under the My Earnings row. Shares the
  // ['earnings'] cache entry with the My Earnings page, so opening the page
  // from this menu renders its header without a second round trip. Chef-only
  // and auth-only for the same reason as the counts above — the endpoint is
  // caller-scoped, and the row it annotates is hidden from non-chefs anyway.
  const { data: earningsRows } = useMyEarnings(
    isAuthenticated && isChef(activeProfile),
  );

  const handleSignOut = async () => {
    await logout();
  };

  // Nothing to navigate to when logged out — the TopBar shows a Sign In
  // button instead of the avatar, so this menu is never opened in that state.
  if (!isAuthenticated || !activeProfile) return null;

  const chef = isChef(activeProfile);
  // Counts are undefined while in flight, which falls back to the chef CTA —
  // the pre-existing behaviour — and flips to the row once they land.
  const hasListings = Boolean(
    listingCounts &&
      listingCounts.active + listingCounts.drafts + listingCounts.past > 0,
  );
  const showKitchen = chef || hasListings;
  // Everything the chef has earned — the same figure as the page's "Total
  // earned" card by construction, since both go through sumEarnedCents. The
  // payouts flow hasn't shipped, so nothing has been drawn against it yet.
  // Held back until the fetch resolves — a sub line that reads $0.00 and then
  // jumps to a real amount is worse than one that arrives late.
  const earnedLine = earningsRows
    ? t('menu.earningsTotal', {
        amount: formatPriceCents(sumEarnedCents(earningsRows), {
          zeroAsAmount: true,
        }),
      })
    : null;
  const nameLine = activeProfile.displayName || activeProfile.email;
  // Secondary line under the name: phone preferred (it's the OTP-verified
  // identifier), email as fallback. Suppressed when it would just repeat the
  // name (a profile with no display name falls back to email on the name line).
  const contactLine = activeProfile.phoneE164 || activeProfile.email;
  const showContact = Boolean(contactLine) && contactLine !== nameLine;

  return (
    <>
    <IonMenu menuId={PROFILE_MENU_ID} contentId="main-content" side="end">
      <IonContent>
        <div className="flex h-full flex-col gap-1 p-4">
          {/* Top row, right-aligned: Share button + language flipper. Share
              opens the reusable QR/share popup; the language dropdown is the
              same one as the Settings page (self-hides with <2 languages). */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-surface-300 bg-background text-foreground px-2 py-1 text-sm font-medium hover:bg-surface-100"
            >
              <Share2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              <span>{tCommon('actions.share')}</span>
            </button>
            <LanguageSwitcher />
          </div>

          {/* Header: display-only (PFP + name + contact). Navigation to the
              profile page is the "My Profile" nav row below, not this block. */}
          <div className="mb-2 flex w-full items-center gap-3 p-2">
            <Avatar src={activeProfile.avatarUrl} name={activeProfile.displayName} size="lg" />
            <div className="min-w-0">
              <p className="truncate font-medium">{nameLine}</p>
              {showContact && (
                <p className="truncate text-sm text-muted-foreground">
                  {contactLine}
                </p>
              )}
            </div>
          </div>

          {NAV_ITEMS.map(({ path, labelKey, Icon }) => {
            // A non-chef with nothing listed has no kitchen yet — swap the My
            // Kitchen row for the chef-application CTA in the same slot (it
            // opens a modal rather than navigating). Once they're a chef, or
            // they've saved so much as a draft, the real row takes over so
            // they always have a way back to it.
            if (path === '/my-kitchen' && !showKitchen) {
              return (
                <IonMenuToggle key={path}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-foreground hover:bg-surface-200"
                    onClick={() => setShowChefModal(true)}
                  >
                    <ChefHat className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{t('actions.applyChef')}</span>
                  </button>
                </IonMenuToggle>
              );
            }
            // Earnings is chef-only — nothing to show a non-chef here (the
            // apply-chef CTA in the /my-kitchen slot above covers the
            // "become a chef" affordance in the same menu).
            if (path === '/my-earnings' && !chef) return null;
            return (
              <IonMenuToggle key={path}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-foreground hover:bg-surface-200"
                  onClick={() => router.push(path, 'forward')}
                >
                  <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block font-medium">{t(labelKey)}</span>
                    {/* Only My Earnings carries a sub line today; the slot is
                        generic so another row can take one without a reshape. */}
                    {path === '/my-earnings' && earnedLine && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {earnedLine}
                      </span>
                    )}
                  </span>
                </button>
              </IonMenuToggle>
            );
          })}

          <div className="mt-auto">
            <IonMenuToggle>
              <IonButton
                expand="block"
                color="danger"
                data-testid="sign-out"
                onClick={handleSignOut}
              >
                {t('actions.signOut')}
              </IonButton>
            </IonMenuToggle>
          </div>
        </div>
      </IonContent>
    </IonMenu>
    <ChefApplicationModal isOpen={showChefModal} onClose={() => setShowChefModal(false)} />
    {/* Opened from inside the drawer, which is a z-index:1000 overlay — the
        share popup must stack above it (and below the global error modal). */}
    <ShareQRModal
      isOpen={qrOpen}
      onClose={() => setQrOpen(false)}
      route="/"
      zIndexClass="z-[1100]"
    />
    </>
  );
};
